import { Eye, EyeOff } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Auth } from "utils/cognito-auth";
import { Form, Formik, FormikConfig } from "formik";
import React, { useState } from "react";
import * as Routes from "routes";
import { boolean, InferType, object, ref, string } from "yup";
import { useMessageState } from "./use-message-state";

export const forgotPasswordValidationSchema = object({
  isDelivered: boolean(),
  email: string().email("Enter a valid email.").required("Email is required."),
  code: string().when("isDelivered", {
    is: true,
    then: string()
      .matches(/^[0-9]+$/, "Must be only digits.")
      .min(6, "Must be exactly 6 digits.")
      .max(6, "Must be exactly 6 digits.")
      .required("Code is required"),
  }),
  newPassword: string().when("isDelivered", {
    is: true,
    then: string()
      .min(8, "New Password should be of minimum 8 characters length")
      .required("New Password is required."),
  }),
  confirmNewPassword: string().when("isDelivered", {
    is: true,
    then: string()
      .oneOf([ref("newPassword"), null], "Passwords must match")
      .required("Confirm New Password is required."),
  }),
});

type ResetPasswordValues = InferType<typeof forgotPasswordValidationSchema>;

export const ResetPassword = () => {
  const [isLoading, setIsLoading] = useState(false);

  const [message, setMessage] = useMessageState({
    passwordReset: false,
    type: null,
    text: "",
  });

  const onSubmit: FormikConfig<
    InferType<typeof forgotPasswordValidationSchema>
  >["onSubmit"] = async (
    { email, isDelivered, code, newPassword },
    { setFieldValue },
  ) => {
    try {
      setIsLoading(true);
      if (!isDelivered) {
        await Auth.forgotPassword(email.toLowerCase());
        setFieldValue("isDelivered", true, false);
        setMessage({
          type: "success",
          text: `A verification code has been successfully sent to ${email}.`,
        });
      } else {
        await Auth.forgotPasswordSubmit(
          email.toLowerCase(),
          code!,
          newPassword!,
        );
        setMessage({
          passwordReset: true,
          type: "success",
          text: `Your password has been successfully reset.`,
        });
      }
    } catch (error: any) {
      setMessage({
        type: "error",
        text: "Could not send password reset code.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const [showPassword, setShowPassword] = useState(false);

  const renderPasswordToggle = () => (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      className="absolute right-1 top-1/2 -translate-y-1/2"
      onClick={() => setShowPassword(!showPassword)}
    >
      {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
    </Button>
  );

  return (
    <Formik<ResetPasswordValues>
      initialValues={{
        isDelivered: false,
        email: "",
        code: "",
        newPassword: "",
        confirmNewPassword: "",
      }}
      validationSchema={forgotPasswordValidationSchema}
      onSubmit={onSubmit}
    >
      {({
        values: { isDelivered, email, code, newPassword, confirmNewPassword },
        handleChange,
        touched,
        errors,
      }) => (
        <Form method="POST">
          <div className="flex flex-col min-w-[100vw] items-center">
            <Card className="min-w-[340px] max-w-[500px] m-2">
              <CardHeader>
                <CardTitle className="text-xl">Password Reset</CardTitle>
                <CardDescription>
                  {isDelivered
                    ? "Complete the form with the 'forgot password' code and a new password."
                    : "Don't worry, it happens to the best of us"}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {message.passwordReset ? (
                  <>
                    <Alert>
                      <AlertDescription>{message.text}</AlertDescription>
                    </Alert>

                    <Separator />
                    <Button variant="link" className="w-full" asChild>
                      <a href={Routes.signIn}>Sign In</a>
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        value={email}
                        onChange={handleChange}
                        disabled={isDelivered || isLoading}
                        className={touched.email && errors.email ? "border-destructive" : ""}
                      />
                      {touched.email && errors.email && (
                        <p className="text-xs text-destructive">{errors.email}</p>
                      )}
                    </div>

                    {isDelivered && (
                      <>
                        <div className="space-y-1.5">
                          <Label htmlFor="code">Code</Label>
                          <Input
                            id="code"
                            name="code"
                            type="text"
                            value={code}
                            onChange={handleChange}
                            disabled={isLoading}
                            className={touched.code && errors.code ? "border-destructive" : ""}
                          />
                          {touched.code && errors.code && (
                            <p className="text-xs text-destructive">{errors.code}</p>
                          )}
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor="newPassword">New Password</Label>
                          <div className="relative">
                            <Input
                              id="newPassword"
                              name="newPassword"
                              type={showPassword ? "text" : "password"}
                              value={newPassword}
                              onChange={handleChange}
                              disabled={isLoading}
                              className={`pr-10 ${touched.newPassword && errors.newPassword ? "border-destructive" : ""}`}
                            />
                            {renderPasswordToggle()}
                          </div>
                          {touched.newPassword && errors.newPassword && (
                            <p className="text-xs text-destructive">{errors.newPassword}</p>
                          )}
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor="confirmNewPassword">
                            Confirm New Password
                          </Label>
                          <div className="relative">
                            <Input
                              id="confirmNewPassword"
                              name="confirmNewPassword"
                              type={showPassword ? "text" : "password"}
                              value={confirmNewPassword}
                              onChange={handleChange}
                              disabled={isLoading}
                              className={`pr-10 ${touched.confirmNewPassword && errors.confirmNewPassword ? "border-destructive" : ""}`}
                            />
                            {renderPasswordToggle()}
                          </div>
                          {touched.confirmNewPassword &&
                            errors.confirmNewPassword && (
                              <p className="text-xs text-destructive">
                                {errors.confirmNewPassword}
                              </p>
                            )}
                        </div>
                      </>
                    )}

                    {isLoading ? (
                      <div className="flex justify-center w-full py-2">
                        <Spinner />
                      </div>
                    ) : (
                      <>
                        {message.text && message.type && (
                          <Alert
                            variant={
                              message.type === "error"
                                ? "destructive"
                                : "default"
                            }
                          >
                            <AlertDescription>{message.text}</AlertDescription>
                          </Alert>
                        )}

                        <Button
                          type="submit"
                          disabled={isLoading}
                          className="w-full"
                        >
                          Submit
                        </Button>

                        <div className="flex justify-center gap-1">
                          <Button variant="link" size="sm" asChild>
                            <a href={Routes.signUp}>Sign up</a>
                          </Button>
                          <Button variant="link" size="sm" asChild>
                            <a href={Routes.signIn}>Sign In</a>
                          </Button>
                          <Button variant="link" size="sm" asChild>
                            <a href={Routes.resendVerification}>
                              Resend Verification
                            </a>
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </Form>
      )}
    </Formik>
  );
};
